export class WebSocketService {
  constructor(options = {}) {
    this.socket = null;
    this.messageCallbacks = new Set();
    this.connectionCallbacks = new Set();
    this.wsUrl = null;

    // Token refresh callback
    this.onTokenRefreshNeeded = options.onTokenRefreshNeeded || null;
    
    // Reconnection settings
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 100;
    this.reconnectInterval = options.reconnectInterval || 1000;
    this.maxReconnectInterval = options.maxReconnectInterval || 20000;
    this.reconnectDecay = options.reconnectDecay || 1.5;
    this.reconnectTimer = null;
    this.isIntentionalClose = false;
  }

  connect(wsUrl) {
    if (this.socket) {
      this.disconnect();
    }

    this.wsUrl = wsUrl;
    this.shouldReconnect = true;
    this.isIntentionalClose = false;
    this.createWebSocket();
  }

  createWebSocket() {
    this.socket = new WebSocket(this.wsUrl);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.connectionCallbacks.forEach(callback => callback(true));
    };

    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.messageCallbacks.forEach(callback => callback(data));
    };

    this.socket.onclose = async (event) => {
      this.connectionCallbacks.forEach(callback => callback(false));

      // If authentication failure
      if (event.code === 4001 && this.onTokenRefreshNeeded){
        // Call the refresh callback
        await this.onTokenRefreshNeeded();
      }
      // Only reconnect on abnormal closures
      else if (!this.isIntentionalClose && this.shouldReconnect && event.code !== 1000 && event.code !== 4003) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      this.shouldReconnect = false;
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Exponential backoff
    const baseDelay = Math.min(
      this.reconnectInterval * Math.pow(this.reconnectDecay, this.reconnectAttempts),
      this.maxReconnectInterval
    );
    
    // Add ±30% jitter to spread out retries
    const jitterFactor = 0.7 + (Math.random() * 0.6); // Random between 0.7 and 1.3
    const delay = Math.floor(baseDelay * jitterFactor);
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.createWebSocket();
    }, delay);
  }

  disconnect() {
    this.isIntentionalClose = true;
    this.shouldReconnect = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  onMessage(callback) {
    this.messageCallbacks.add(callback);
    return () => this.messageCallbacks.delete(callback);
  }

  onConnectionChange(callback) {
    this.connectionCallbacks.add(callback);
    return () => this.connectionCallbacks.delete(callback);
  }

  sendMessage(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}
